import React, { useEffect, useState } from 'react';
import LogRocket from 'logrocket';
import styles from './Main.module.scss';
import { Header } from '../../components/Header/Header';
import { Footer } from '../../components/Footer/Footer';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Home } from '../Home/Home';
import ApiService from '../../services/ApiService';
import SystemService from '../../services/SystemService';
import { log } from '../../services/LogService';
import ErrorAlert from '../../components/ErrorAlert/ErrorAlert';
import { ErrorPage } from '../../components/ErrorPage/ErrorPage';
import { Backdrop, CircularProgress } from '@mui/material';
import SpinnerService, { SpinnerStatusType } from '../../services/SpinnerService';
import { ErrorType } from '../../services/ErrorBoundaryService';
import DynamicTabSystem from '../DynamicTabSystem/DynamicTabSystem';
import UnitService from '../../services/UnitService';
import { GenericLoadingScreen } from '../../components/GenericLoadingScreen/GenericLoadingScreen';
import { getAccessToken, getBasePath, getTokenClaims, getUserFromCookie } from '../../utils/Utils';
import Constants from '../../utils/Constants';
import { Shortcuts } from '../Shortcuts/Shortcuts';

export const Main = () => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<SpinnerStatusType>({
    pendingApiResponses: 0,
    isIframeLoaded: true,
  });

  const handleCookies = async () => {
    const userCookie = getUserFromCookie();
    const accessToken = getAccessToken();
    const claims = getTokenClaims(accessToken);
    const expirationTime = claims?.exp || 0; // Okta returns this value in seconds
    const tokenIsExpired = Date.now() > expirationTime * 1000;

    if (!accessToken || !userCookie || tokenIsExpired) {
      const encodedUri = encodeURI(process.env.REACT_APP_LEGACY_DASHBOARD_REDIRECT_URL + window.location.href);
      window.location.replace(`${process.env.REACT_APP_LOGIN_REDIRECT_URL}${encodedUri}`);
    } else {
      LogRocket.identify(claims.sub as string);
      ApiService.init(accessToken, userCookie.locale);
      log(`API Initialized with locale ${userCookie.locale}`);
      await UnitService.fetchUnits();
      setLoaded(true);
    }
  };

  useEffect(() => {
    SystemService.clearSystemData();
    const main = async () => {
      await handleCookies();
    };
    main();
    SpinnerService.getSpinnerStatus(setShowSpinner);
  }, []);

  const systemsRoute = `${Constants.routes.systems}/:systemId`;
  const shortcutsRoute = `${Constants.routes.shortcuts}/:shortcutKey`;

  return loaded ? (
    <>
      <Backdrop color="secondary" open={showSpinner.pendingApiResponses > 0} className={styles['loading-backdrop']}>
        <CircularProgress color="primary" />
      </Backdrop>
      <ErrorAlert />
      <Header />
      <BrowserRouter basename={getBasePath()} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <main className={styles['main-container']} data-testid="main-container">
          <Routes>
            <Route path={systemsRoute} element={<DynamicTabSystem />} />
            <Route path={shortcutsRoute} element={<Shortcuts />} />
            {/* <Route path="/sysprojs" element={<SystemProjectsSummary />} /> */}
            <Route path="/" element={<Home />} />
            <Route path="*" element={<ErrorPage errorType={ErrorType.SystemNotFound} />} />
          </Routes>
        </main>
      </BrowserRouter>
      <Footer />
    </>
  ) : (
    <GenericLoadingScreen />
  );
};
